<template>
    <div
        v-if="consultations"
        class="container-fluid userGroupConsultationsWrapper">
        <div class="d-block d-md-flex justify-content-between mb-3">
            <div class="d-block d-md-flex flex-wrap align-items-center">
                <div
                    v-if="group.is_viewable_authorizer"
                    class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="c-freeword input-group input-group-sm">
                        <input
                            v-model="search.freeword"
                            @blur="onChangeSearchFreeword"
                            type="text"
                            class="form-control"
                            placeholder="フリーワード"
                            style="min-width: 100px;">
                        <div class="input-group-append">
                            <div
                                class="c-freeword__btn input-group-text"
                                @click="onChangeSearchFreeword">
                                検索
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="group.is_viewable_authorizer"
                    class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.medical_course_id"
                            @change="onChangeSearchMedicalCourseId"
                            style="min-width: 100px;"
                            class="form-control">
                            <option :value="null">{{ $locale.consultation_host }}科で検索</option>
                            <option
                                v-for="course in medicalCourses"
                                :value="course.id">
                                {{ course.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div
                    v-if="isOrganization"
                    class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.child_group_id"
                            @change="onChangeSearchChildGroupId"
                            id="status"
                            style="min-width: 100px;"
                            class="form-control">
                            <option :value="null">{{ `${this.childGroupName}で検索` }}</option>
                            <option
                                v-for="childGroup in group.child_groups"
                                :value="childGroup.id">
                                {{ childGroup.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.status"
                            @change="onChangeSearchStatus"
                            id="status"
                            style="min-width: 100px;"
                            class="form-control">
                            <option :value="null">ステータスで検索</option>
                            <option
                                v-for="status in consultationSearchStatuses"
                                :value="searchStatusValue(status)">
                                {{ searchStatusText(status) }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="d-block d-md-flex align-items-center">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">{{ $locale.consultation_host }}日</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="c-freeword input-group input-group-sm">
                            <input
                                v-model="search.start_time"
                                @change="onChangeSearchStartTime"
                                type="date"
                                class="form-control"
                                aria-label="Recipient's username"
                                aria-describedby="basic-addon2"
                                style="min-width: 100px;">
                        </div>
                    </div>
                    <span class="small mt-2 ml-2">〜</span>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="c-freeword input-group input-group-sm">
                            <input
                                v-model="search.end_time"
                                @change="onChangeSearchEndTime"
                                type="date"
                                class="form-control"
                                aria-label="Recipient's username"
                                aria-describedby="basic-addon2"
                                style="min-width: 100px;">
                        </div>
                    </div>
                </div>

                <div
                    v-if="group.is_health_package"
                    class="mt-2 mr-0 ml-0 mb-2 mr-md-2 ml-md-3 mb-md-0">
                    <div class="custom-control custom-checkbox mt-2">
                        <input
                            v-model="search.is_only_health_package"
                            id="is_only_health_package"
                            class="custom-control-input"
                            type="checkbox"
                            @change="onChangeSearchIsOnlyHealthPackage">
                        <label
                            class="custom-control-label"
                            for="is_only_health_package">
                            健康管理パッケージの{{ $locale.consultation_host }}のみ表示
                        </label>
                    </div>
                </div>
            </div>

            <div class="mt-2 text-right">
                <b-button
                    variant="yokumiru-orange"
                    @click="downloadCSV"
                    :disabled="inProgress"
                    style="min-width: 142px;">
                    CSVダウンロード
                </b-button>
            </div>
        </div>

        <div class="d-flex flex-wrap mb-1 ml-1">
            <div v-if="hasReservedByDoctor" class="reserved-notice">
                <div></div>
                <div>&nbsp;:&nbsp;医師による予約</div>
            </div>
        </div>

        <div class="row justify-content-center">
            <div class="col-md-12">
                <b-table
                    striped
                    hover
                    responsive
                    :items="consultations"
                    :fields="fields"
                    :sort-by.sync="morphState.sortBy"
                    :sort-desc.sync="morphState.sortDesc"
                    @sort-changed="fetchConsultations"
                    class="table-bordered m-table">
                    <template v-slot:cell(id)="data">
                        <div :class="{ 'reserved-by-doctor' : Number( data.item.flg_reserved_by_doctor ) === 1 }">
                            {{ data.item.id }}
                        </div>
                    </template>

                    <template v-slot:cell(start_time)="data">
                        <div class="consultationTime">
                            <span>
                                {{ helper.dateForHuman(data.item.start_time, timezone, { exceptYear: helper.isCurrentYear(data.item, timezone), simpleYear: true }) }}
                            </span>
                            <span :class="helper.dayForHumanCSS(data.item.start_time , timezone)">
                                {{ helper.dayForHuman(data.item.start_time, timezone, { bracket: true }) }}
                            </span>
                            <span class="ml-2">
                                {{ helper.timeForHuman(data.item, timezone) }}
                            </span>
                            <span v-if="data.item.doctor_menu !== null">
                                {{ `（${data.item.doctor_menu.consultation_minutes}分）` }}
                            </span>
                        </div>
                        <timezone-box-jst
                            v-if="timezone !== 'Asia/Tokyo'"
                            :consultation="data.item"
                            :timezone="timezone"
                            except-year>
                        </timezone-box-jst>
                    </template>

                    <template v-slot:cell(medical_courses.name)="data">
                        <template v-if="data.item.doctor_menu">
                            <div style="line-height:1.3; margin-top:2px;">
                                {{ data.item.medical_course.name }}
                            </div>
                            <div
                                v-if="data.item.doctor_menu.is_health_package"
                                class="doctorMenuHealthPackageIcon">
                                <img src="/images/common/label_health_package1.png">
                            </div>
                            <a href="#" @click="showModalDoctorMenu(data.item.doctor_menu_id, data.item, $event)">
                                {{ data.item.doctor_menu.title }}
                            </a>
                            <div
                                v-if="data.item.doctor_menu_id !== '[FILTERED]' && data.item.is_repeat_reserved"
                                class="repeatReservedLabel">
                                再
                            </div>
                        </template>
                        <template v-else>
                            <div>{{ data.item.medical_course.name }}</div>
                        </template>
                    </template>

                    <template v-slot:cell(doctors.full_name_kana)="data">
                        <template v-if="data.item.doctor">
                            <a href="#" @click="showModalDoctorProfile(data.item.doctor_id, $event)">
                                {{ data.item.doctor.full_name }}
                            </a>
                            <div class="small">{{ data.item.doctor.full_name_kana }}</div>
                        </template>
                        <template v-else-if="data.item.doctor_id === null">
                            <span class="text-muted">未定</span>
                        </template>
                        <template v-else>
                            <span class="text-muted">退会済の医師</span>
                        </template>
                    </template>

                    <template v-slot:cell(join_users.full_name_kana)="data">
                        <template v-if="data.item.user">
                            <div>{{ data.item.user.full_name }}</div>
                            <div class="small">{{ data.item.user.full_name_kana }}</div>
                            <span class="text-muted small">#{{ data.item.user_id }}</span>
                        </template>
                        <template v-else>
                            <span class="text-muted">退会済の{{ $locale.patient }}</span>
                        </template>
                    </template>

                    <template v-slot:cell(child_groups.name_kana)="data">
                        <div v-if="data.item.child_group !== null">
                            <a :href="`/${helper.groupTypeModel(group.group_type)}/groups/${data.item.child_group.id}`">
                                {{ data.item.child_group.name }}
                            </a>
                        </div>
                    </template>

                    <template v-slot:cell(plan_id)="data">
                        <div>{{ data.item.plan.name }}</div>
                    </template>

                    <template v-slot:cell(status)="data">
                        <div
                            class="c-indicator"
                            :class="detectStatusCSS( data.item )">
                            <span>{{ detectStatusName(data.item) }}</span>
                            <span>/</span>
                            <span>{{ detectStatusNameMedicalRecord(data.item) }}</span>
                        </div>
                        <div class="d-flex mt-1">
                            <div
                                v-show="data.item.messages_count > 0"
                                class="c-counter-badge mr-2">
                                <b-icon-chat-dots-fill></b-icon-chat-dots-fill>
                            </div>
                            <div
                                v-show="data.item.images_count > 0"
                                class="c-counter-badge mr-2">
                                <img src="/images/common/icon_camera2_bk.svg" style="width: 14px; position: relative; top: -1px;">
                                <span class="c-counter-badge__item">{{ data.item.images_count }}</span>
                            </div>
                            <div
                                v-show="data.item.attached_images_count > 0"
                                class="c-counter-badge">
                                <b-icon-images></b-icon-images>
                                <span class="c-counter-badge__item">{{ data.item.attached_images_count }}</span>
                            </div>
                        </div>
                    </template>

                    <template v-slot:cell(estimated_total)="data">
                        <div v-if="data.item.doctor_menu && data.item.doctor_menu.is_health_package">
                            <!-- 健康管理パッケージの場合は金額非表示 -->
                        </div>
                        <div v-else-if="data.item.estimated_total !== null">
                            {{ data.item.estimated_total.toLocaleString() }}円
                        </div>
                        <div v-else-if="data.item.invoice">
                            {{ data.item.invoice.total.toLocaleString() }}円
                        </div>
                    </template>

                    <template v-slot:cell(medical_records.published_at)="data">
                        <div
                            v-if="data.item.medical_record && data.item.medical_record.published_at"
                            class="u-palt">
                            <span>
                                {{ helper.dateForHuman(data.item.medical_record.published_at, timezone, { simpleYear: true }) }}
                            </span>
                            <span
                                class="small font-weight-bold"
                                :class="helper.dayForHumanCSS(data.item.medical_record.published_at, timezone)">
                                {{ helper.dayForHuman(data.item.medical_record.published_at, timezone, { bracket: true }) }}
                            </span>
                            <span>{{ helper.timeForHuman(data.item.medical_record.published_at, timezone) }}</span>
                        </div>
                    </template>

                    <template v-slot:head(consultation_feedback.status)="data">
                        <div class="small font-weight-bold">フィードバックの<br>ステータス</div>
                    </template>
                    <template v-slot:cell(consultation_feedback.status)="data">
                        <div
                            v-if="data.item.consultation_feedback && data.item.consultation_feedback.status !== null"
                            class="feedbackLabel"
                            :style="feedbackLabel(data.item.consultation_feedback).css">
                            {{ feedbackLabel(data.item.consultation_feedback).text }}
                        </div>
                    </template>

                    <template v-slot:cell(consultation_feedback.next_schedule)="data">
                        <div v-if="data.item.consultation_feedback" class="feedbackNextSchedule">{{ data.item.consultation_feedback.next_schedule }}</div>
                    </template>

                    <template v-slot:cell(consultation_detail)="data">
                        <b-button
                            v-if="data.item.consultation_feedback && data.item.consultation_feedback.published_at !== null"
                            class="btn btn-info btn-sm"
                            variant="yokumiru-dark-green"
                            @click="downloadFeedback(data.item)"
                            :disabled="inProgress">
                            記録をダウンロード
                        </b-button>
                    </template>
                </b-table>

                <div class="d-flex flex-wrap justify-content-between align-items-center">
                    <b-pagination
                        :total-rows="morphState.total"
                        :per-page="morphState.per_page"
                        v-model="morphState.current_page"
                        @change="fetchConsultations">
                    </b-pagination>

                    <div class="d-flex flex-wrap align-items-center">
                        <div class="mr-3 mb-3">
                            <span class="small">{{ dispPaginationDetail }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <doctor-menu-modal user-type="user"/>
        <doctor-profile-modal/>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { sprintf } from 'sprintf-js'
import moment from 'moment-timezone'
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'

export default {
    props: {
        timezone: {
            type: String,
            required: true,
        },
        medicalCourses: {
            type: Array,
            required: true,
        },
        parentGroup: {
            type: Object|null,
            default: null,
        },
        group: {
            type: Object,
            required: true,
        },
        childGroupName: {
            type: String,
            default: '法人',
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            search: {
                freeword: null,
                medical_course_id: null,
                child_group_id: null,
                status: null,
                start_time: null,
                end_time: null,
                is_only_health_package: false,
            },
            consultations: null,
            consultationStatus: constants.consultations,
            feedbackStatusAll: constants.consultation_feedback.all,
            inProgress: false,
            errors: {},
            fields: [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                },
                {
                    key: 'start_time',
                    label: `${this.$locale.consultation_host}日時`,
                    sortable: true,
                },
                {
                    key: 'medical_courses.name',
                    label: `${this.$locale.consultation_host}科・メニュー`,
                    sortable: true,
                },
                {
                    key: 'doctors.full_name_kana',
                    label: '医師',
                    sortable: true,
                },
                {
                    key: 'join_users.full_name_kana',
                    label: this.$locale.patient,
                    sortable: true,
                },
                {
                    key: 'child_groups.name_kana',
                    label: this.childGroupName,
                    sortable: true,
                },
                {
                    'key': 'plan_id',
                    'label': 'サービス',
                    sortable: true,
                },
                {
                    key: 'status',
                    label: 'ステータス',
                    sortable: true,
                },
                {
                    key: 'medical_records.published_at',
                    label: '記録公開日時',
                    sortable: true,
                },
            ],
        }
    },

    computed: {
        isCompany: function () {
            return this.helper.isCompany(this.group)
        },

        isOrganization: function () {
            return this.helper.isOrganization(this.group)
        },

        morphState: function () {
            if (this.isCompany) {
                return this.$store.state.userCompanyConsultations
            }
            else if (this.isOrganization) {
                return this.$store.state.userOrganizationConsultations
            }
            return null
        },

        /**
         * 医師による予約の存在判定
         *
         * @return {boolean}
         */
        hasReservedByDoctor: function () {
            if (!this.consultations) return false

            return !!this.consultations.find(consultation => {
                return Number(consultation.flg_reserved_by_doctor) === 1
            })
        },

        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            if (this.morphState.total < 1) return ''
            return `全${this.morphState.total}件中 ${this.morphState.from}～${this.morphState.to}件表示`
        },

        /**
         * 検索用ステータスリストを返す
         *
         * @return {object}
         */
        consultationSearchStatuses: function () {
            const statuses = this.consultationStatus.all

            statuses.splice(3, 0, { value: 'cancel-all', text: 'キャンセル' })
            statuses.splice(8, 0, { value: 'absence-all', text: '未入室終了' })
            statuses.push({ value: 'finish-no-report', text: `${this.$locale.consultation_host}終了（未）` })
            statuses.push({ value: 'finish-report', text: `${this.$locale.consultation_host}終了（記）` })

            return statuses
        },
    },

    created: function () {
        this.initQueryFromState()
        this.initSearchCondition()
        this.fetchConsultations()

        if (this.isCompany) {
            if (!this.parentGroup) {
                this.fields.push({
                    key: 'estimated_total',
                    label: '金額',
                    sortable: true,
                })
            }
            if (this.group.is_health_package) {
                this.fields.push({
                    key: 'consultation_feedback.status',
                    label: 'フィードバックのステータス',
                    sortable: true,
                })
                this.fields.push({
                    key: 'consultation_feedback.next_schedule',
                    label: `次回${this.$locale.consultation_host}予定`,
                    sortable: false,
                })
                this.fields.push({
                    key: 'consultation_detail',
                    label: '',
                    class: 'text-center',
                    sortable: false,
                })
            }
        }
    },

    methods: {
        ...mapMutations({
            setQueryCompany: 'userCompanyConsultations/setQuery',
            setQueriesCompany: 'userCompanyConsultations/setQueries',
            clearQueriesCompany: 'userCompanyConsultations/clearQueries',
            setQueryOrganization: 'userOrganizationConsultations/setQuery',
            setQueriesOrganization: 'userOrganizationConsultations/setQueries',
            clearQueriesOrganization: 'userOrganizationConsultations/clearQueries',
        }),

        setQuery: function (data) {
            if (this.isOrganization) {
                this.setQueryOrganization(data)
            }
            else if (this.isCompany) {
                this.setQueryCompany(data)
            }
        },

        setQueries: function (data) {
            if (this.isOrganization) {
                this.setQueriesOrganization(data)
            }
            else if (this.isCompany) {
                this.setQueriesCompany(data)
            }
        },

        clearQueries: function () {
            if (this.isOrganization) {
                this.clearQueriesOrganization()
            }
            else if (this.isCompany) {
                this.clearQueriesCompany()
            }
        },

        /**
         * 検索条件の初期化
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (this.isCompany && data.userCompanyConsultations) {
                    this.setQueries(data.userCompanyConsultations)
                }
                else if (this.isOrganization && data.userOrganizationConsultations) {
                    this.setQueries(data.userOrganizationConsultations)
                }
            }
        },

        /**
         * 検索条件の初期化
         */
        initSearchCondition: async function () {
            this.search.freeword = this.morphState.freeword
            this.search.medical_course_id = this.morphState.medical_course_id
            this.search.child_group_id = this.morphState.child_group_id
            this.search.status = this.morphState.status
            this.search.start_time = this.morphState.start_time
            this.search.end_time = this.morphState.end_time
            this.search.is_only_health_package = this.morphState.is_only_health_package

            // 検索する子グループが存在しない場合
            if (!this.group.child_groups || this.group.child_groups.every((group) => group.id !== this.search.child_group_id)) {
                this.search.child_group_id = null
                await this.setQuery({ key: 'child_group_id', value: null })
            }

            // 健康管理パッケージを利用しない場合
            if (!this.group.is_health_package) {
                this.search.is_only_health_package = false
                await this.setQuery({ key: 'is_only_health_package', value: false })
            }
        },

        onChangeSearchFreeword: async function () {
            await this.setQuery({ key: 'freeword', value: this.search.freeword })
            await this.fetchConsultations(1)
        },

        onChangeSearchMedicalCourseId: async function () {
            await this.setQuery({ key: 'medical_course_id', value: this.search.medical_course_id })
            await this.fetchConsultations(1)
        },

        onChangeSearchChildGroupId: async function () {
            await this.setQuery({ key: 'child_group_id', value: this.search.child_group_id })
            await this.fetchConsultations(1)
        },

        onChangeSearchStatus: async function () {
            await this.setQuery({ key: 'status', value: this.search.status })
            await this.fetchConsultations(1)
        },

        onChangeSearchStartTime: async function () {
            await this.setQuery({ key: 'start_time', value: this.search.start_time })
            await this.fetchConsultations(1)
        },

        onChangeSearchEndTime: async function () {
            await this.setQuery({ key: 'end_time', value: this.search.end_time })
            await this.fetchConsultations(1)
        },

        onChangeSearchIsOnlyHealthPackage: async function () {
            await this.setQuery({ key: 'is_only_health_package', value: this.search.is_only_health_package })
            await this.fetchConsultations(1)
        },

        /**
         * 検索条件ステータス値
         * @param status
         * @return {*}
         */
        searchStatusValue: function (status) {
            return status.value
        },

        /**
         * 検索条件ステータス表記
         * @param status
         * @return {*}
         */
        searchStatusText: function (status) {
            let text = status.text

            if (this.$locale.type === 'consul') {
                text = text.replace('診療', '相談')
                text = text.replace('患者', '会員')
            }
            return text
        },

        /**
         * 相談一覧取得
         *
         * @param {number|object} param
         */
        fetchConsultations: async function (param = null) {
            if (this.inProgress) return
            this.inProgress = true

            this.consultations = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.user.groups.endpoint.base

            await axios.get(`${endpoint}/${this.group.id}/consultations${this.buildQuery()}`)
                .then(({data}) => {
                    this.consultations = data.data
                    this.setQueries(data.meta)
                })
                .catch(({response}) => {
                    if (response.status === 422) {
                        this.clearQueries()
                    }
                })

            this.inProgress = false
        },

        /**
         * QueryString生成
         *
         * @param {boolean} paginate
         * @return {string}
         */
        buildQuery: function (paginate = true) {
            let query = '?'

            const state = this.morphState

            if (paginate) {
                query += `page=${state.current_page}`
                query += `&sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }
            else {
                query += `sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }

            if (state.freeword.length > 0) {
                query += `&freeword=${state.freeword}`
            }
            if (state.medical_course_id) {
                query += `&medical_course_id=${state.medical_course_id}`
            }
            if (state.child_group_id) {
                query += `&child_group_id=${state.child_group_id}`
            }
            if (state.status) {
                query += `&status=${state.status}`
            }
            if (state.start_time) {
                query += `&start_time=${ moment(state.start_time).set({'hour' : 0 , 'minute' : 0 , 'second' : 0 }).format( 'YYYY-MM-DD HH:mm:ss' ) }`;
            }
            if (state.end_time) {
                query += `&end_time=${ moment(state.end_time).set({'hour' : 23 , 'minute' : 59 , 'second' : 59 }).format( 'YYYY-MM-DD HH:mm:ss' ) }`;
            }
            if (state.is_only_health_package) {
                query += '&is_only_health_package=1'
            }

            return query
        },

        /**
         * ステータス表記
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusCSS: function (consultation) {
            const result = this.consultationStatus.all.find(status => {
                return status.value === consultation.status
            })

            // 診療終了
            if (result.value === this.consultationStatus.finished) {
                if (!consultation.medical_record ||
                    !consultation.medical_record.body ||
                    consultation.medical_record.body.length < 1) {
                    return 'c-indicator--yellow'
                }
            }
            return result.css
        },

        /**
         * ステータス表記
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusName: function (consultation) {
            let result = this.consultationStatus.all.find(status => {
                return status.value === consultation.status
            })

            if (this.$locale.type === 'consul') {
                result.text = result.text.replace('診療', '相談')
                result.text = result.text.replace('患者', '会員')
            }

            // キャンセル
            if (consultation.flg_cancel == 1) {
                if (constants.consultations.cancelAll.includes(result.value)) {
                    return result.text
                }
                if (consultation.flg_cancel_by_doctor == 1) {
                    return 'キャンセル（医師）'
                }
                return this.$locale.type === 'consul' ? 'キャンセル（会員）' : 'キャンセル（患者）'
            }

            // その他
            return result.text
        },

        /**
         * ステータス表記（相談記録）
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusNameMedicalRecord: function (consultation) {
            if (consultation.medical_record && consultation.medical_record.published_at) {
                return '記録公開済'
            }
            return '記録未公開'
        },

        /**
         * フィードバックのステータス名
         *
         * @param {object} feedback
         * @return {object}
         */
        feedbackLabel: function (feedback) {
            const label = this.feedbackStatusAll.find((status) => {
                return Number(status.value) === Number(feedback.status)
            })

            return label || {}
        },

        /**
         * 医師メニュー詳細モーダル表示
         *
         * @param {number} doctorMenuId
         * @param {object} consultation
         * @param {object} e
         */
        showModalDoctorMenu: function (doctorMenuId, consultation, e) {
            e.preventDefault()
            bus.$emit('open-modal-doctor-menu', doctorMenuId, consultation)
        },

        /**
         * 医師詳細モーダル表示
         *
         * @param {number} doctorId
         * @param {object} e
         */
        showModalDoctorProfile: function (doctorId, e) {
            e.preventDefault()
            bus.$emit('open-modal-doctor-profile', doctorId)
        },

        /**
         * CSVダウンロード
         */
        downloadCSV: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.groups.endpoint.base
            const options = { responseType : 'blob' }

            await axios.get(`${endpoint}/${this.group.id}/consultations/export${this.buildQuery(false)}`, options)
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(
                        blob,
                        sprintf(
                            '%s_%s一覧_%s.csv',
                            this.group.name,
                            this.$locale.consultation_host,
                            moment().format('YYYYMMDD')
                        )
                    )
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            this.inProgress = false
        },

        /**
         * 法人フィードバック記録PDFダウンロード
         *
         * @param {object} consultation
         */
        downloadFeedback: async function (consultation) {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = `${constants.user.consultation_feedback.endpoint.base}/${consultation.consultation_feedback.id}/export`
            const options = { responseType : 'blob' }

            await axios.get(endpoint, options)
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(
                        blob,
                        sprintf(
                            '#%s_%s記録表_%s.pdf',
                            consultation.id,
                            this.$locale.consultation_host,
                            moment().format('YYYYMMDD')
                        )
                    )
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            this.inProgress = false
        },
    },
}
</script>

<style lang="scss" scoped>
.reserved-notice {
    display: flex;
    align-items: center;
    position: relative;
    top: 2px;

    div:nth-child(1) {
        width: 16px;
        height: 12px;
        background: #417daf;
        border-radius: 4px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}

.reserved-by-doctor {
    display: inline-block;
    padding: 0 4px;
    color: #fff;
    background: #417daf;
    border-radius: 4px;
}

.repeatReservedLabel {
    position: relative;
    top: -1px;
    display: inline-block;
    width: 18px;
    height: 18px;
    line-height: 18px;
    color: #fff;
    font-size: 10px;
    text-align: center;
    background-color: #e7a222;
    border-radius: 50%;
}

.consultationTime {
    color: #464a4d;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.4;
    font-feature-settings: 'palt';

    span:last-child {
        color: #6e7275;
        font-weight: normal;
    }
}

.doctorMenuHealthPackageIcon {
    margin: 3px 0 1px;

    img {
        width: 130px;
    }
}

.feedbackLabel {
    width: 90px;
    margin: 0 auto;
    padding: 3px 0;
    border-radius: 20px;
    color: #fff;
    font-size: 13px;
    text-align: center;
    box-sizing: border-box;
}

.feedbackNextSchedule {
    overflow-x: scroll;
    white-space: pre;
    width: 160px;
    padding: 0 0 5px;
}
</style>

<style lang="scss">
.userGroupConsultationsWrapper {
    table {
        min-width: 1400px;

        th, td {
            font-size: 0.75rem;
        }

        th {
            &:nth-child(9) {
                width: 140px;
            }

            &:nth-child(10) {
                width: 160px;
            }
        }
    }
}
</style>
